import {
  Component,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AbbAvatarFieldComponent } from '@components/abb-form/abb-avatar-field.component';
import { RouterModule } from '@angular/router';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { BehaviorSubject, Subscription } from 'rxjs';
import Validation from 'app/utils/validation';
import { AbbAddressFieldComponent } from '@components/abb-form/abb-address-field.component';
import { IUser } from '@services/cognito/cognito.service';
import { AbbIconComponent } from '@components/abb-icon/abb-icon.component';
import { OverflowTooltipDirective } from '@directives/overflow-tooltip.directive';
import { TranslateService } from '@ngx-translate/core';
import { UserAccount } from 'gql/graphql';

@Component({
  selector: 'abb-signup-form',
  standalone: true,
  template: `
    <form
      id="signup-form"
      [formGroup]="form"
      (ngSubmit)="onSubmit()"
      class="w-full h-full flex flex-col flex-1 justify-between pt-2"
    >
      <div class="w-full h-full flex flex-col flex-1 gap-2 relative">
        @if(mode() == 'edit' || (secondStepRegistration()?.value ||
        twoStepsRegistration()?.value)) {
        <mat-form-field
          [ngClass]="mode() == 'edit' ? 'w-full' : 'bg-white-field w-1/2'"
          color="primary"
        >
          <mat-label>{{ 'Name' | t }}</mat-label>
          <input matInput formControlName="name" name="name" />
          @if(form.get('name')?.hasError('required')){
          <mat-error>{{ 'This field is required' | t }}</mat-error>
          }
        </mat-form-field>
        <mat-form-field
          [ngClass]="mode() == 'edit' ? 'w-full' : 'bg-white-field w-1/2'"
          color="primary"
        >
          <mat-label>{{ 'Last name' | t }}</mat-label>
          <input matInput formControlName="lastName" name="lastName" />
          @if(form.get('lastName')?.hasError('required')){
          <mat-error>{{ 'This field is required' | t }}</mat-error>
          }
        </mat-form-field>
        @if(form.contains('imgUrl')) {
        <div
          class="flex flex-col justify-center self-center absolute top-[3%] right-0"
        >
          <abb-avatar-field
            formControlName="imgUrl"
            [circularBtn]="true"
          ></abb-avatar-field>
        </div>
        }

        <div class="w-full flex flex-row 2xl:flex-col justify-between">
          <mat-form-field
            class="w-1/2 2xl:w-full"
            [ngClass]="{ 'bg-white-field': mode() == 'new' }"
            color="primary"
          >
            <mat-label>{{ 'Email address' | t }}</mat-label>
            <input matInput formControlName="email" name="email" />
            @if(form.get('email')?.hasError('required')){
            <mat-error>{{ 'This field is required' | t }}</mat-error>
            } @if(form.get('email')?.hasError('email') ||
            form.get('email')?.hasError('pattern')){
            <mat-error>{{ 'Email is not valid (e.g., user@example.com)' | t }}</mat-error>
            }
          </mat-form-field>
          <mat-form-field
            class="w-5/12 2xl:w-full"
            [ngClass]="{ 'bg-white-field': mode() == 'new' }"
            color="primary"
          >
            <mat-label>{{ 'Phone' | t }}</mat-label>
            <input matInput formControlName="phone" name="phone" />
            @if(form.get('phone')?.hasError('required')){
            <mat-error>{{ 'This field is required' | t }}</mat-error>
            } @else if(form.get('phone')?.hasError('phone')){
            <mat-error>{{ 'Phone is not valid (e.g., +39...)' | t }}</mat-error>
            }
          </mat-form-field>
        </div>
        <mat-form-field
          class="w-full"
          [ngClass]="{ 'bg-white-field': mode() == 'new' }"
          color="primary"
        >
          <mat-label>{{ 'Company / Legal Entity' | t }}</mat-label>
          <input matInput formControlName="company" name="company" />
          @if (form.get('company')?.hasError('required')) {
          <mat-error>{{ 'This field is required' | t }}</mat-error>
          } @if (form.get('company')?.hasError('minlength')) {
          <mat-error>{{ 'Minimum 3 characters are required' | t }}</mat-error>
          } @if (form.get('company')?.hasError('maxlength')) {
          <mat-error>{{ 'Maximum 50 characters allowed' | t }}</mat-error>
          } @if (form.get('company')?.hasError('whitespace')) {
          <mat-error>{{
            'Leading and trailing spaces are not allowed' | t
          }}</mat-error>
          }
        </mat-form-field>
        @if(form.contains('password')) {
        <mat-form-field
          class="w-full"
          [ngClass]="{ 'bg-white-field': mode() == 'new' }"
          color="primary"
        >
          <mat-label>{{ 'Password' | t }}</mat-label>
          <input
            matInput
            formControlName="password"
            name="password"
            [type]="hidePsw() ? 'password' : 'text'"
          />
          <abb-icon
            matSuffix
            (click)="hidePsw.set(!hidePsw())"
            class="cursor-pointer pr-4"
            [name]="hidePsw() ? 'mat/visibility_off' : 'mat/visibility'"
          />
          @if(form.get('password')?.hasError('required')){
          <mat-error>{{ 'This field is required' | t }}</mat-error>
          } @else if(form.get('password')?.hasError('minlength')){
          <mat-error>{{
            'Password must be at least 8 characters long' | t
          }}</mat-error>
          } @else if(form.get('password')?.hasError('maxlength')){
          <mat-error>{{
            'Password must not exceed 256 characters' | t
          }}</mat-error>
          } @else if(form.get('password')?.hasError('password')){
          <mat-error class="truncate" overflowTooltip>{{
            'Password must include uppercase and lowercase letters, a number, and a special character (e.g., !@#$%^&*())'
              | t
          }}</mat-error>
          }
        </mat-form-field>
        } @if(form.contains('confirmPassword')) {
        <mat-form-field
          class="w-full"
          [ngClass]="{ 'bg-white-field': mode() == 'new' }"
          color="primary"
        >
          <mat-label>{{ 'Confirm Password' | t }}</mat-label>
          <input
            matInput
            formControlName="confirmPassword"
            name="confirmPassword"
            [type]="hideCnfPsw() ? 'password' : 'text'"
          />
          <abb-icon
            matSuffix
            (click)="hideCnfPsw.set(!hideCnfPsw())"
            class="cursor-pointer pr-4"
            [name]="hideCnfPsw() ? 'mat/visibility_off' : 'mat/visibility'"
          />
          @if(form.get('confirmPassword')?.hasError('required')){
          <mat-error>{{ 'This field is required' | t }}</mat-error>
          } @if (form.get('confirmPassword')?.hasError('matching')) {
          <mat-error>{{ 'Confirm Password does not match' | t }}</mat-error>
          }
        </mat-form-field>
        } }
      </div>
      @if(mode() == 'new' && secondStepRegistration()?.value){
      @if(form.contains('acceptTerms')){
      <div class="inline-flex flex-col min-w-0 text-left mt-10">
        <mat-checkbox
          formControlName="acceptTerms"
          name="acceptTerms"
          color="primary"
          class="border-font-checkbox no-hover-checkbox rounded-checkbox"
        >
          <a
            (click)="downloadTermsDocument($event)"
            class="text-abb-primary hover:underline font-semibold"
            >{{ 'Accept terms & conditions' | t }}</a
          >
          *
        </mat-checkbox>
        <div
          class="box-border w-full relative text-xs before:inline-block before:h-4"
        >
          <div
            class="absolute top-0 left-0 right-0 px-4 opacity-100 translate-y-0"
          >
            @if(formSubmitted() &&
            form.get('acceptTerms')?.hasError('required')){
            <mat-error>{{ 'This field is required' | t }}</mat-error>
            }
          </div>
        </div>
      </div>
      <p
        class="text-xs text-abb-font/60 text-center mt-4"
        [innerHTML]="'privacy notice' | t"
      ></p>

      } }
    </form>
  `,
  host: {
    class: 'flex flex-col flex-1',
  },
  styles: `
    .bg-white-field {
        --mdc-filled-text-field-container-color: var(--abb-color-white);
        --mat-form-field-state-layer-color: transparent;
    }
    .text-black {
        --mdc-form-field-label-text-color: var(--abb-color-black);
    }
    ::ng-deep .rounded-checkbox .mdc-checkbox__background {
        border-radius: 4px !important;
    }
    .no-hover-checkbox {
        --mdc-checkbox-selected-focus-state-layer-color: var(--abb-color-white);
        --mdc-checkbox-selected-hover-state-layer-color: var(--abb-color-white);
        --mdc-checkbox-selected-pressed-state-layer-color: var(--abb-color-white);
        --mdc-checkbox-unselected-focus-state-layer-color: var(--abb-color-white);
        --mdc-checkbox-unselected-hover-state-layer-color: var(--abb-color-white);
        --mdc-checkbox-unselected-pressed-state-layer-color: var(--abb-color-white);
    }
  `,
  imports: [
    CommonModule,
    TPipe,
    RouterModule,
    AbbAvatarFieldComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    OverflowTooltipDirective,
    AbbIconComponent,
  ],
  providers: [{ provide: TRANSLATION_PREFIX, useValue: 'signup' }],
})
export class SignupFormComponent implements OnInit, OnDestroy {
  mode = input<'new' | 'edit'>('new');
  initValues = input<
    Omit<IUser, 'password' | 'confirmPassword' | 'acceptTerms'> | UserAccount
  >();
  disableEmail = input<boolean>(false);
  hidePsw = signal(true);
  hideCnfPsw = signal(true);
  formValidationStatusChange = output<boolean>();
  twoStepsRegistration = input<BehaviorSubject<boolean>>();
  secondStepRegistration = input<BehaviorSubject<boolean>>();
  checkFirstStepValidation = input<BehaviorSubject<boolean>>();
  firstStepValidationPassed = output<boolean>();
  formSubmit = output<FormGroup>();
  formSubmitted = signal<boolean>(false);
  form: FormGroup<{
    name: FormControl<string | null>;
    lastName: FormControl<string | null>;
    imgUrl?: FormControl<string | null>;
    company: FormControl<string | null>;
    registrationCompany?: FormControl<string | null>;
    country?: FormControl<string | null>;
    city?: FormControl<string | null>;
    postalCode?: FormControl<string | null>;
    address?: FormControl<string | null>;
    fullAddress?: FormControl<string | null>;
    phone: FormControl<string | null>;
    email: FormControl<string | null>;
    password?: FormControl<string | null>;
    confirmPassword?: FormControl<string | null>;
    acceptTerms?: FormControl<boolean | null>;
  }>;

  private subscriptions: Subscription[] = [];
  constructor(
    private fb: FormBuilder,
    private readonly translate: TranslateService
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      company: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(50),
          Validation.noWhitespaceValidator,
        ],
      ],
      phone: ['', [Validators.required, Validation.field('phone')]],
      email: [
        '',
        [
          Validators.required,
          Validators.email,
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
          ),
        ],
      ],
    });
    this.subscriptions.push(
      this.form.valueChanges.subscribe(() => {
        this.formValidationStatusChange.emit(!this.form.valid);
      })
    );
  }

  ngOnInit(): void {
    if (this.mode() == 'new') {
      this.form.addControl('imgUrl', new FormControl(''));
      this.form.addControl(
        'registrationCompany',
        new FormControl({ value: '', disabled: true })
      );
      this.form.addControl(
        'password',
        new FormControl('', [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(256),
          Validation.field('password'),
        ])
      );
      this.form.addControl(
        'confirmPassword',
        new FormControl('', [Validators.required])
      );
      this.form.addControl(
        'acceptTerms',
        new FormControl(false, [Validators.requiredTrue])
      );

      this.form.addValidators([
        Validation.match('password', 'confirmPassword'),
      ]);

      if (this.checkFirstStepValidation() instanceof BehaviorSubject) {
        this.subscriptions.push(
          (
            this.checkFirstStepValidation() as BehaviorSubject<boolean>
          ).subscribe((value) => {
            if (value === true) {
              const firstStepFields = [
                'name',
                'lastName',
                'imgUrl',
                'phone',
                'email',
                'password',
                'confirmPassword',
                'company',
              ];
              if (
                firstStepFields.some(
                  (f) => (this.form.controls as any)[f].invalid
                )
              ) {
                if (
                  firstStepFields.some(
                    (f) => (this.form.controls as any)[f].errors?.required
                  )
                ) {
                  firstStepFields.forEach((f) => {
                    const control = (this.form.controls as any)[f];
                    if (control.errors?.required) {
                      control.markAsTouched();
                    }
                  });
                }
                this.firstStepValidationPassed.emit(false);
              } else {
                this.firstStepValidationPassed.emit(true);
              }
            }
          })
        );
      }
    } else {
      this.form.setValue({
        name: this.initValues()?.name!,
        lastName: this.initValues()?.lastName!,
        company: this.initValues()?.company!,
        phone: this.initValues()?.phone!,
        email: this.initValues()?.email!,
      });
      if (this.disableEmail()) {
        this.form.get('email')?.disable();
      }
    }
  }

  downloadTermsDocument(event: MouseEvent) {
    // Previene la navigazione standard
    event.preventDefault();
    event.stopPropagation();

    // Ottieni la lingua corrente
    const currentLang = this.translate.currentLang || 'en-EN';
    let documentPath = '';
    if (currentLang === 'it-IT') {
      documentPath = 'assets/terms_and_condtions/ABB Ability GTC-it.pdf';
    } else {
      // Default per en-EN e altre lingue
      documentPath = 'assets/terms_and_condtions/ABB Ability GTC-en.pdf';
    }

    // Crea un elemento link temporaneo per scaricare il file
    const link = document.createElement('a');
    link.href = documentPath;
    link.download = `terms-conditions-${currentLang}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.open(documentPath, '_blank');
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  onSubmit(): void {
    this.formSubmitted.set(true);
    this.formSubmit.emit(this.form);
  }
}
